// This browser's settings beyond contrast (the Settings page; the Mac's Settings): kept in localStorage under the Mac's
// UserDefaults names, and read live, so a change on the Settings page reaches every open view.
import { useSyncExternalStore } from 'react';

const listeners = new Set<() => void>();

function read(key: string, initial: boolean): boolean {
  try {
    const raw = localStorage.getItem(`ws:${key}`);
    return raw === null ? initial : raw === 'true';
  } catch {
    return initial;
  }
}

function write(key: string, value: boolean) {
  try {
    localStorage.setItem(`ws:${key}`, String(value));
  } catch {
    // kept for this page only
  }
  for (const fn of listeners) fn();
}

// A change made in another tab of this browser (Settings open beside the writing) reaches this one too: the
// browser's own `storage` event, which never fires in the tab that wrote.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === null || e.key.startsWith('ws:')) for (const fn of listeners) fn();
  });
}

function useSetting(key: string, initial: boolean): boolean {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => read(key, initial),
    () => initial,
  );
}

/** Settings ▸ Writing ▸ Show formatting marks (the Mac's `showsFormattingMarks`): emphasis asterisks, escapes and span
 *  brackets drawn faintly in the text instead of hidden. */
export const useShowsFormattingMarks = () => useSetting('showsFormattingMarks', false);
export const setShowsFormattingMarks = (on: boolean) => write('showsFormattingMarks', on);

/** Settings ▸ Appearance ▸ Steady lines in mind maps (the Mac's `mapSteadyLines`): the maps' charcoal and pencil drawn
 *  without their wobble. */
export const useMapSteadyLines = () => useSetting('mapSteadyLines', false);
export const setMapSteadyLines = (on: boolean) => write('mapSteadyLines', on);
